import { type ClassValue, clsx } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * tailwind-merge only de-duplicates class groups it recognises. Our semantic
 * scales (globals.css) add names it has never heard of — `rounded-card`,
 * `shadow-floating`, `z-toast`, `max-w-page` — so without registering them a
 * component doing cn('rounded-card', className) would emit BOTH its own radius
 * and the caller's override, and source order would silently decide the winner.
 * That is the bug that makes a design token feel unreliable and sends people
 * back to hardcoding.
 *
 * Keep in sync with the scale blocks in globals.css.
 */
const RADII = ['chip', 'control', 'card', 'panel', 'pill'];

const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      rounded: [{ rounded: RADII }],
      // The per-edge groups matter as much as the all-corners one. A bottom
      // sheet rounds only its top (`rounded-t-panel`), and tailwind-merge
      // treats an unregistered `rounded-t-panel` as an unknown class it must
      // never drop: cn('rounded-t-panel', 'rounded-card') returned BOTH, so a
      // caller could not override the sheet's radius at all. Stock names
      // (`rounded-t-2xl` vs `rounded-xl`) already resolve correctly — the
      // named scale has to be told.
      'rounded-t': [{ 'rounded-t': RADII }],
      'rounded-r': [{ 'rounded-r': RADII }],
      'rounded-b': [{ 'rounded-b': RADII }],
      'rounded-l': [{ 'rounded-l': RADII }],
      'shadow': [{ shadow: ['subtle', 'raised', 'floating', 'overlay', 'glow-gold', 'struck'] }],
      z: [{ z: ['base', 'raised', 'rail', 'nav', 'overlay', 'modal', 'toast'] }],
      'max-w': [{ 'max-w': ['page', 'content', 'form'] }],
      // The type scale is worse than the others if left unregistered, because
      // `text-` is two namespaces at once. tailwind-merge's text-COLOR group
      // accepts any bare word, so it claims `text-body` — and cn('text-body',
      // 'text-gold') returns just 'text-gold', silently dropping the font size.
      // Registering the seven steps under font-size is what stops a type token
      // from being eaten by the colour next to it.
      'font-size': [{ text: ['display', 'title', 'heading', 'body', 'caption', 'label', 'micro'] }],
    },
  },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatNumber(num: number): string {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toLocaleString();
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

// Admin/system timezone (Eastern US)
export const ADMIN_TIMEZONE = 'America/New_York';

/**
 * Convert a datetime-local input value (browser local time) to a full ISO string.
 * datetime-local gives "2026-03-15T19:00" with no timezone info.
 * new Date("2026-03-15T19:00") treats it as LOCAL time, so .toISOString() gives the correct UTC.
 */
export function localDatetimeToISO(datetimeLocal: string): string {
  return new Date(datetimeLocal).toISOString();
}

/**
 * Convert a UTC ISO string to a datetime-local input value in the browser's local timezone.
 * Used to populate edit forms so the admin sees times in their local timezone.
 */
export function isoToLocalDatetime(iso: string): string {
  const d = new Date(iso);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

// Format date in user's local timezone
export function formatDate(date: Date | string): string {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// Format time in user's local timezone
export function formatTime(date: Date | string): string {
  return new Date(date).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
}

// Format date/time in user's local timezone (for user-facing pages)
export function formatDateTime(date: Date | string): string {
  return new Date(date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

// Format date/time in EST (for admin pages)
export function formatDateTimeEST(date: Date | string): string {
  return new Date(date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: ADMIN_TIMEZONE,
    timeZoneName: 'short',
  });
}

export function formatRelativeTime(date: Date | string): string {
  const now = new Date();
  const target = typeof date === 'string' ? new Date(date) : date;
  const diff = now.getTime() - target.getTime();

  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 30) return formatDate(target);
  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return 'Just now';
}

export function formatCountdown(targetDate: Date | string): string {
  const now = new Date();
  const target = typeof targetDate === 'string' ? new Date(targetDate) : targetDate;
  const diff = target.getTime() - now.getTime();

  if (diff <= 0) return 'Expired';

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

/**
 * Smart event timing label using both start and end times.
 * Returns { label, isLive } for display.
 */
export function getEventTiming(startTime: Date | string, endTime: Date | string): { label: string; isLive: boolean } {
  const now = new Date();
  const start = typeof startTime === 'string' ? new Date(startTime) : startTime;
  const end = typeof endTime === 'string' ? new Date(endTime) : endTime;

  if (now < start) {
    // Not started yet — countdown to start
    return { label: `Starts ${formatCountdown(start)}`, isLive: false };
  }

  if (now >= start && now < end) {
    // Currently live — countdown to end
    const remaining = formatCountdown(end);
    return { label: remaining === 'Expired' ? 'Live' : `${remaining} left`, isLive: true };
  }

  // Past end time
  return { label: 'Ended', isLive: false };
}

export function generateInviteCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export function calculateLevel(totalXp: number, xpPerLevel: number = 1000): number {
  return Math.floor(totalXp / xpPerLevel) + 1;
}

export function calculateXpForNextLevel(currentXp: number, xpPerLevel: number = 1000): number {
  return xpPerLevel - (currentXp % xpPerLevel);
}

export function calculateLevelProgress(currentXp: number, xpPerLevel: number = 1000): number {
  return (currentXp % xpPerLevel) / xpPerLevel;
}

export function getOrdinalSuffix(num: number): string {
  const j = num % 10;
  const k = num % 100;
  if (j === 1 && k !== 11) return num + 'st';
  if (j === 2 && k !== 12) return num + 'nd';
  if (j === 3 && k !== 13) return num + 'rd';
  return num + 'th';
}

export function truncateAddress(address: string, chars: number = 4): string {
  return `${address.slice(0, chars + 2)}...${address.slice(-chars)}`;
}

export function randomBetween(min: number, max: number): number {
  return Math.random() * (max - min) + min;
}

export function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

const ROMAN_NUMERALS: [number, string][] = [
  [1000, 'M'], [900, 'CM'], [500, 'D'], [400, 'CD'],
  [100, 'C'], [90, 'XC'], [50, 'L'], [40, 'XL'],
  [10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I'],
];

export function toRoman(num: number): string {
  if (num <= 0 || num > 3999) return String(num);
  let result = '';
  let remaining = num;
  for (const [value, numeral] of ROMAN_NUMERALS) {
    while (remaining >= value) {
      result += numeral;
      remaining -= value;
    }
  }
  return result;
}

export function formatEdition(serial: number, total: number): string {
  return `EDITION ${toRoman(serial)} OF ${toRoman(total)}`;
}
